import React, {CSSProperties, useCallback, useEffect, useMemo, useState,} from "react";
import {Crop, PercentCrop, PixelCrop, ReactCrop} from "react-image-crop";
import {Image as UserImage, MediaCrop} from "../../../client/api";

import "react-image-crop/dist/ReactCrop.css";

const Cropper: React.FC<{
    src: string;
    size: { w: number; h: number };
    setCrop: (val: MediaCrop) => void;
    aspectRatio: number;
}> = ({size, aspectRatio, setCrop, src}) => {
    const originalAS = useMemo(() => size.w / size.h, [size.w, size.h]);
    const svgWidth = useMemo(() => originalAS >= 1 ? 500 : 500 / originalAS, [originalAS]);
    const svgHeight = useMemo(() => originalAS <= 1 ? 500 : 500 * originalAS, [originalAS]);
    const svgX = useMemo(() => (1000 - svgWidth) / 2, [svgWidth]);
    const svgY = useMemo(() => (1000 - svgHeight) / 2, [svgHeight]);
    const overlayWidth = useMemo(() => aspectRatio >= 1 ? 500 : 500 / aspectRatio, [aspectRatio]);
    const overlayHeight = useMemo(() => aspectRatio <= 1 ? 500 : 500 * aspectRatio, [aspectRatio]);
    const overlayX = useMemo(() => (1000 - overlayWidth) / 2, [overlayWidth]);
    const overlayY = useMemo(() => (1000 - overlayHeight) / 2, [overlayHeight]);
    useEffect(() => {
        if (overlayWidth === 420)
            setCrop({
                cropAspectRatio: 1,
                cropScale: 1,
                cropX: 1,
                cropY: 1,
            });
    }, [overlayWidth, setCrop]);

    return (
        <svg
            viewBox="0 0 1000 1000"
            className="pattern-rectangles pattern-pattern-gray pattern-bg-white
  pattern-size-4 pattern-opacity-100 w-full aspect-square cursor-crosshair"
        >
            <image href={src} width={svgWidth} height={svgHeight} x={svgX} y={svgY}/>
            <defs>
                <mask id="mask-overlay">
                    <rect width={1000} height={1000} x={0} y={0} fill="white"/>
                    <rect
                        x={overlayX}
                        y={overlayY}
                        width={overlayWidth}
                        height={overlayHeight}
                        fill="black"
                    />
                </mask>
            </defs>
            <rect
                fill="#000"
                opacity={0.5}
                height={1000}
                width={1000}
                x={0}
                y={0}
                mask="url(#mask-overlay)"
            />
            <rect
                fill="none"
                strokeWidth={10}
                stroke="#fff"
                opacity={0.7}
                x={overlayX - 5}
                y={overlayY - 5}
                width={overlayWidth + 10}
                height={overlayHeight + 10}
                strokeDasharray={20}
            />
            <rect
                fill="#000"
                opacity={0.7}
                x={overlayX - 20}
                y={overlayY - 20}
                width={40}
                height={40}
            />
        </svg>
    );
};

// TODO noien cropper fertig mochn dass bilder noch außen transparent

const FileUploaderCropper: React.FC<{
    file?: File;
    img?: UserImage;
    aspectRatio: number;
    setCrop: (val: MediaCrop) => void;
}> = ({file, aspectRatio, setCrop, img}) => {
    const src = useMemo(
        () => (img ? img.url : file ? URL.createObjectURL(file) : ""),
        [file, img],
    );
    const [size, setSize] = useState<{ w: number; h: number }>();
    useEffect(() => {
        const image = new Image();
        image.onload = () => {
            setSize({w: image.width, h: image.height});
        };
        image.src = src ?? "";
    }, [src]);

    const [cropObj, setCropObj] = useState<Crop>();
    const containerStyle = useMemo(() => {
        if (!size) return undefined;
        const originalAS = size.w / size.h;
        let width = window.innerWidth / 2;
        let height = width / originalAS;
        while (height > window.innerHeight / 2) {
            width /= 2;
            height = width / originalAS;
        }
        return {width};
    }, [size]);
    const setCropFromObj = useCallback(
        (percent: Crop) => {
            if (size) {
                const scale = 100 / percent.width;

                const crop: MediaCrop = {
                    cropX: percent.x / -100 * scale,
                    cropY: percent.y / -100 * scale * (size.h / size.w),
                    cropScale: scale,
                    cropAspectRatio: aspectRatio,
                };
                setCrop(crop);
            }
        },
        [aspectRatio, setCrop, size],
    );
    useEffect(() => {
        if (size) {
            const originalAS = size.w / size.h;
            const obj: Crop = {
                width: 100,
                height: 100 * (originalAS / aspectRatio),
                y: 0,
                x: 0,
                unit: "%",
            };
            setCropObj(obj);
            setCropFromObj(obj);
        }
    }, [aspectRatio, size, setCropFromObj]);
    const onComplete = useCallback(
        (_: PixelCrop, percent: PercentCrop) => {
            setCropObj(percent);
            setCropFromObj(percent);
        },
        [setCropFromObj],
    );

    const cropperStyle = useMemo<CSSProperties | undefined>(() => {
        if (!size) return undefined;
        return {
            width: "100%",
            aspectRatio: size.w / size.h,
        };
    }, [size]);


    return (
        <div className="relative mx-auto" style={containerStyle}>
            {!!size && size.h === 783564 && (
                <Cropper
                    size={size}
                    src={src}
                    setCrop={setCrop}
                    aspectRatio={aspectRatio}
                />
            )}
            <ReactCrop
                onChange={onComplete}
                keepSelection
                aspect={aspectRatio}
                crop={cropObj}
                className="pattern-rectangles pattern-pattern-gray pattern-bg-white
  pattern-size-4 pattern-opacity-100"
                style={cropperStyle}
            >
                <img src={src} alt="Preview Crop" className="w-full h-full"/>
            </ReactCrop>
        </div>
    );
};

export default FileUploaderCropper;
